import { Alert } from '@/global/components/Alert'
import Card from '@/global/components/Card'
import ControlledTextField from '@/global/components/Form/Base/ControlledTextField'
import IconeAjuda from '@/global/components/IconeAjuda'
import { useAuth } from '@/hooks'
import { cn } from '@/lib/utils'
import { CategoriaEnum, separateWords } from '@acme/auth'
import { Origens, OrigensEnum } from '@acme/common'
import { SN, StatusAprovacao } from '@acme/core'
import { Grid, IconButton, TextField } from '@mui/material'
import PreviewAnexo, { isImage } from 'global/components/Anexo'
import { FormField } from 'global/components/Form'
import Mapa from 'global/components/Map/Mapa'
import { DownloadIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { Endereco, local, Registro, registros } from 'service/api'
import { uploads } from 'service/api/endpoints/uploads'
import { match } from 'ts-pattern'

interface IExtraInfo {
    endereco_real?: string
}

export function FormularioDetalhesRegistro() {
    const {
        decoded: { categoria },
    } = useAuth()
    const { watch, setValue } = useFormContext<Registro & IExtraInfo>()
    const [urlFacial, setUrlFacial] = useState<string>()

    const {
        latitude,
        longitude,
        statusAprovacao,
        transactionID,
        empresaId,
        metodoRegistro: metodo,
        fileUrl: anexoRegistro,
        Local,
        ...registro
    } = watch()
    const metodoRegistro = metodo?.trim() || undefined

    const exibirMapa =
        !!String(latitude ?? '').length && !!String(longitude ?? '').length
    useEffect(() => {
        if (exibirMapa && !registro.endereco_real)
            getGeocodeByLatLng(+latitude!, +longitude!).then(address => {
                if (!new RegExp(/-?\d+\.\d+,\s?-?\d+\.\d+/).test(address))
                    setValue('endereco_real', address)
                else
                    setValue(
                        'endereco_real',
                        `https://www.google.com/maps/search/?api=1&query=${address.replace(
                            /\s/,
                            '',
                        )}`,
                    )
            })
    }, [exibirMapa])

    useEffect(() => {
        if (transactionID && urlFacial === undefined)
            registros
                .get<string>(`/imagem/${transactionID}`, { empresaId })
                .then(res => setUrlFacial(res))
    }, [transactionID])

    const mensagem =
        categoria === CategoriaEnum.Funcionario
            ? 'você pode excluir este registro'
            : 'este registro pode ser excluído pelo funcionário'

    return (
        <Grid container spacing={2}>
            {statusAprovacao === StatusAprovacao.REPROVADO &&
                !registro.deletedAt && (
                    <Grid item xs={12}>
                        <Alert severity="error" className="w-full">
                            Este registro foi reprovado e não será considerado
                            para cálculo de ponto. Além disso, {mensagem} a
                            qualquer momento sem requerer justificativa.
                        </Alert>
                    </Grid>
                )}
            <Grid item xs={12}>
                <StatusCerca registro={{ ...registro, Local }} />
            </Grid>
            {!!registro.deletedAt && <DetalhesExclusao />}
            <Grid item xs={3}>
                <FormField
                    name="data"
                    label="Data"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    disabled
                />
            </Grid>
            <Grid item xs={3}>
                <FormField
                    name="hora"
                    label="Hora"
                    type="time"
                    InputLabelProps={{ shrink: true }}
                    hiddenClock
                    disabled
                />
            </Grid>
            <Grid item xs={6}>
                <FormField name="origem" label="Origem" disabled />
            </Grid>
            {!!metodoRegistro && (
                <Grid item xs={12}>
                    <ControlledTextField
                        name="metodo_registro"
                        label="Método de registro"
                        variant="filled"
                        value={separateWords(metodoRegistro)
                            .replace(/_/g, ' ')
                            .replace(/^\w/, c => c.toUpperCase())}
                        disabled
                    />
                </Grid>
            )}
            {match(statusAprovacao)
                .with(StatusAprovacao.PENDENTE, () => (
                    <Grid item xs={12}>
                        <FormField
                            name="justificativa"
                            label="Motivo da inclusão"
                            multiline
                            disabled
                        />
                    </Grid>
                ))
                .with(StatusAprovacao.PENDENTE_EXCLUSAO, () => (
                    <Grid item xs={12}>
                        <FormField
                            name="MotivoExclusao.motivo"
                            label="Motivo da exclusão"
                            multiline
                            disabled
                        />
                    </Grid>
                ))
                .with(StatusAprovacao.REPROVADO, () => (
                    <Grid item xs={12}>
                        <FormField
                            name="justificativa"
                            label="Motivo da reprovação"
                            multiline
                            disabled
                        />
                    </Grid>
                ))
                .otherwise(
                    () =>
                        !!registro.justificativa?.trim() && (
                            <Grid item xs={12}>
                                <FormField
                                    name="justificativa"
                                    label="Justificativa"
                                    multiline
                                />
                            </Grid>
                        ),
                )}
            {!!Local && [
                <Grid item xs={9.5} key="nome">
                    <FormField
                        name="Local.nomeLocal"
                        label="Local para registro"
                        disabled
                        InputProps={{
                            endAdornment: !!Local.endereco && (
                                <IconeAjuda size="small">
                                    {Local?.endereco}
                                </IconeAjuda>
                            ),
                        }}
                    />
                </Grid>,
                <Grid item xs={2.5} key="distancia">
                    <TextField
                        label="Distância"
                        value={match(registro.distanciaRegistro)
                            .with('0', () => 'Exata')
                            .with(null, undefined, () => '')
                            .when(
                                x => Number(x) > 999,
                                x =>
                                    `${new Intl.NumberFormat('pt-BR', {
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 2,
                                    }).format(+x / 1000)} km`,
                            )
                            .otherwise(x => x + ' metros')}
                        disabled
                        variant="filled"
                    />
                </Grid>,
            ]}
            {exibirMapa && [
                <Grid item xs={12} key="endereco_real">
                    <FormField
                        name="endereco_real"
                        label="Endereço real do registro"
                        disabled
                    />
                </Grid>,
                <Grid item xs={12} sx={{ mt: -3 }} key="mapa">
                    <Card className="p-2">
                        <h6 className="text-lg font-semibold text-center">
                            Localização
                        </h6>
                        <div className="w-full h-auto flex justify-center">
                            <Mapa
                                latitude={+latitude!}
                                longitude={+longitude!}
                            />
                        </div>
                    </Card>
                </Grid>,
            ]}
            {urlFacial && (
                <Grid item xs={12}>
                    <Card className="p-2 flex justify-center items-center flex-col">
                        <h6 className="text-lg font-semibold text-center">
                            Selfie
                        </h6>
                        <img
                            src={urlFacial}
                            alt={String(transactionID)}
                            style={{ width: '16rem', height: 'auto' }}
                            loading="lazy"
                        />
                    </Card>
                </Grid>
            )}
            {!!anexoRegistro && (
                <Grid item xs={12}>
                    <Card className="p-2 flex justify-center items-center flex-col relative">
                        <h6 className="text-lg font-semibold text-center">
                            Anexo
                        </h6>
                        <div
                            className={cn(
                                'w-full',
                                isImage(anexoRegistro.split('.').at(-1)!)
                                    ? 'max-w-[16rem]'
                                    : 'max-w-[36rem]',
                            )}
                        >
                            <PreviewAnexo
                                source={`${uploads.URI}/${anexoRegistro}`}
                                ext={anexoRegistro.split('.').at(-1)!}
                            />
                        </div>
                        <IconButton
                            sx={{ position: 'absolute', top: 0, right: 4 }}
                            href={`${uploads.URI}/${anexoRegistro}`}
                            target="_blank"
                            rel="external"
                            title="Baixar anexo"
                        >
                            <DownloadIcon />
                        </IconButton>
                    </Card>
                </Grid>
            )}
        </Grid>
    )
}

function DetalhesExclusao() {
    const { watch } = useFormContext()

    const [deletedAt, excluidoPor, motivoExclusao] = watch([
        'deletedAt',
        'excluidoPor',
        'motivoExclusao',
    ])

    return (
        <>
            <Grid item xs={3}>
                <TextField
                    label="Data da exclusão"
                    type="date"
                    value={deletedAt}
                    disabled
                    fullWidth
                />
            </Grid>
            <Grid item xs={9}>
                <TextField
                    label="Excluído pelo usuário"
                    defaultValue={excluidoPor}
                    disabled
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                />
            </Grid>
            <Grid item xs={12} sx={{ mb: 2 }}>
                <TextField
                    multiline
                    rows={4}
                    defaultValue={motivoExclusao ?? 'Não informado'}
                    fullWidth
                    disabled
                    label="Motivo da exclusão"
                />
            </Grid>
        </>
    )
}

async function getGeocodeByLatLng(lat: number, lng: number) {
    return local.endereco
        .get<Endereco>({ latitude: lat, longitude: lng })
        .then(res => res.endereco)
}

function StatusCerca({ registro }: { registro: Partial<Registro> }) {
    const isDentroCerca = (() => {
        if (registro.dentroCerca == null) return null
        if (
            // Alguns registros vêm com a informação em texto
            ![
                OrigensEnum.APLICATIVO_PONTO,
                OrigensEnum.APLICATIVO_PONTO_COLETIVO,
                Origens[7],
                Origens[10],
            ].includes(registro.origem as any)
        )
            return null
        if (!registro.Local?.id) return null
        if (!registro.distanciaRegistro || registro.distanciaRegistro === '0')
            return null
        return SN.isTrue(registro.dentroCerca)
    })()

    if (isDentroCerca === null) return null

    return (
        <Alert
            className={cn(
                'w-full mb-4 text-white [&_svg]:text-white',
                isDentroCerca ? 'bg-green-600' : 'bg-error',
            )}
            severity={isDentroCerca ? 'success' : 'error'}
        >
            {isDentroCerca
                ? 'Dentro da cerca eletrônica'
                : 'Fora da cerca eletrônica'}
        </Alert>
    )
}
